import { message } from "antd";
import { AxiosResponse } from "axios";
import { MESSAGE } from "constant";
import i18n from "../i18n/index";
import { htmlToText } from "html-to-text";

export const OtherUtil = {
  getFirstChar,
  loadCallback,
  loadResponse,
  loadError,
  convertSlugUnicode,
  getFullname,
  getFullAddress,
  getEnumValues,
  nonAccentVietnamese,
  isImageUrl,
  getBaseUrl,
  convertToText,

  /* map */
  validateCoordinates,
  iframeMapByCoordinates,
};

// Converting standard Vietnamese Characters to non-accent ones. Example: hải -> hai
// convert Tiếng Việt thành không dấu dùng cho so sánh
function nonAccentVietnamese(str: string) {
  if (!str) return "";
  str = str.toLowerCase();
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
  str = str.replace(/đ/g, "d");
  // Some system encode vietnamese combining accent as individual utf-8 characters
  str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ""); // Huyền sắc hỏi ngã nặng
  str = str.replace(/\u02C6|\u0306|\u031B/g, ""); // Â, Ê, Ă, Ơ, Ư
  return str;
}

function getEnumValues<T = any>(input: T) {
  return Object.keys(input as any).filter((i) => isNaN(Number(i)));
}

function getFirstChar(str: string): string {
  return str.charAt(0);
}

function loadCallback<T extends (...P: any[]) => any>(cb?: T, ...data: Parameters<T>): ReturnType<T> {
  return cb && cb(...data);
}

function loadResponse<T>(response: void | AxiosResponse<any, any>): T | null {
  if (
    response instanceof Object &&
    response.data &&
    response.status &&
    response.status >= 200 &&
    response.status < 300
  ) {
    return response.data?.data;
  }

  return null;
}

function loadError(
  error: any,
  opt?: {
    hiddenServerError?: boolean;
  }
) {
  console.log(error);
  if (error) {
    if (error.response) {
      let description: string = error.response.data.message;

      if (!!error?.response?.data?.errors[0]) {
        message.error(error?.response?.data?.errors[0] as string);
      } else if (!Boolean(opt?.hiddenServerError)) {
        message.error(i18n.t(`${description}` || `INTERNAL_SERVER_ERROR`));
      }
      return error.response;
    } else message.error(MESSAGE.ERROR.NETWORK);
  } else {
    message.error(MESSAGE.ERROR.NETWORK);
  }
}

function convertSlugUnicode(str: string): string {
  // Chuyển hết sang chữ thường
  str = str.toLowerCase();

  // xóa dấu
  str = str
    .normalize("NFD") // chuyển chuỗi sang unicode tổ hợp
    .replace(/[\u0300-\u036f]/g, ""); // xóa các ký tự dấu sau khi tách tổ hợp

  // Thay ký tự đĐ
  str = str.replace(/[đĐ]/g, "d");

  // Xóa ký tự đặc biệt
  str = str.replace(/([^0-9a-z-\s])/g, "");

  // Xóa khoảng trắng thay bằng ký tự -
  str = str.replace(/(\s+)/g, "-");

  // Xóa ký tự - liên tiếp
  str = str.replace(/-+/g, "-");

  // xóa phần dư - ở đầu & cuối
  str = str.replace(/^-+|-+$/g, "");

  // return
  return str;
}

function getFullAddress(
  address: string | undefined | null,
  ward: string | undefined | null,
  province: string | undefined | null,
  city: string | undefined | null
) {
  let fullAddress: string[] = address ? [address.trim()] : [];
  if (!address && !ward && !province && !city) return null;
  if (ward) {
    fullAddress.push(ward.trim());
  }
  if (province) {
    fullAddress.push(province.trim());
  }
  if (city) {
    fullAddress.push(city.trim());
  }
  return fullAddress.join(", ");
}

// Generate name by language
function getFullname(first: string | undefined | null, last: string | undefined | null, lang?: string): string {
  const currLang = lang ? lang : i18n.language || "en";

  let fullName: string = first?.trim() || "";

  if (last && currLang === "en") {
    fullName += " " + last.trim();
  } else if (last && currLang === "vn") {
    fullName = last.trim() + " " + fullName;
  }

  fullName = fullName.trim();
  return fullName;
}

/**
 *
 * @param input [10.930049865182662, 106.74422805720212]
 * @returns Boolean
 */
function validateCoordinates(input: string): boolean {
  const coordinates = input.split(",");

  if (coordinates.length !== 2) {
    return false; // Không phải cặp tọa độ
  }

  const lat = parseFloat(coordinates[0].trim());
  const lng = parseFloat(coordinates[1].trim());

  if (isNaN(lat) || isNaN(lng)) {
    return false; // Không phải số hợp lệ
  }

  const latRange = -90 <= lat && lat <= 90;
  const lngRange = -180 <= lng && lng <= 180;

  return latRange && lngRange;
}

/**
 *
 * @param input { lat: 10.930049865182662, lng: 106.74422805720212 }
 * @returns Boolean
 */
function iframeMapByCoordinates(position: { lat: number; lng: number }): string {
  return `https://maps.google.com/maps?q=${position.lat},${position.lng}&output=embed`;
}

// Hàm kiểm tra xem một chuỗi có phải là URL hình ảnh hay không
function isImageUrl(url: string) {
  return /(https?:\/\/.*\.(?:png|jpg|jpeg|gif|svg|webp))/i.test(url);
}

// Hàm lấy base URL (không bao gồm các tham số truy vấn)
function getBaseUrl(url: string) {
  try {
    const urlObj = new URL(url);
    return urlObj.origin + urlObj.pathname;
  } catch (e) {
    return url; // Trả về URL gốc nếu không hợp lệ
  }
}

function convertToText(html: any) {
  return htmlToText(html, {
    wordwrap: false,
    // Xử lý các thẻ <img>
    selectors: [
      {
        selector: "img",
        // format: "text",
        options: {
          // Sử dụng thuộc tính alt nếu có, ngược lại sử dụng src
          baseElement: "img",
          // Hàm tùy chỉnh để định dạng hình ảnh
          format: (node: any, walk: any, builder: any) => {
            const alt = node.attribs.alt;
            const src = node.attribs.src;
            if (alt) {
              builder.addInline(`[Hình ảnh: ${alt}]`);
            } else if (src) {
              builder.addInline(`[Hình ảnh: ${src}]`);
            } else {
              builder.addInline("[Hình ảnh]");
            }
          },
        },
      },
    ],
  });
}
